import initAligningGuidelines from "@/utils/canvas/aligningGuidelines";
import initCenteringGuidelines from "@/utils/canvas/centeringGuidelines";
import { canvasWidth, canvasAspectRatio } from "@/constants/canvas";
import { trackEvent } from "@/utils/tracking";

/**
 * Canvas Manipulation Mixin
 * Handles canvas transformations, scaling, rotation, and resizing
 */
export default {
  data() {
    return {
      canvasWidthPx: null,
      canvasHeightPx: null,
    };
  },
  methods: {
    scaleCanvas(event) {
      if (!this.initialized) return;

      if (typeof this.resetCardPan === "function") {
        this.resetCardPan();
      }

      const scaleFactor = event.target?.value ?? event;
      const canvasX = this.config.frontConfig.canvas.width * scaleFactor;
      const canvasY = this.config.frontConfig.canvas.height * scaleFactor;

      if (!window.frontCanvas || !window.frontCanvas.lowerCanvasEl) return;

      window.frontCanvas.setWidth(canvasX);
      window.frontCanvas.setHeight(canvasY);
      this.canvasWidthPx = canvasX;
      this.canvasHeightPx = canvasY;
      window.frontCanvas.setZoom(canvasX / canvasWidth);
      window.frontCanvas.renderAll();

      if (window.backCanvas && window.backCanvas.lowerCanvasEl) {
        window.backCanvas.setWidth(canvasX);
        window.backCanvas.setHeight(canvasY);
        window.backCanvas.setZoom(canvasX / canvasWidth);
        window.backCanvas.renderAll();
      }

      if (typeof this.checkCardOverflow === "function") {
        this.$nextTick(() => this.checkCardOverflow());
      }
    },

    showFrontSide() {
      if (window.activeCanvas === window.backCanvas) {
        trackEvent({
          eventName: "Customizer",
          eventType: "Click",
          action: "SwitchSide",
          details: { side: "front" },
        });
        this.rotateCard();
      }
    },

    showBackSide() {
      if (window.activeCanvas === window.frontCanvas) {
        trackEvent({
          eventName: "Customizer",
          eventType: "Click",
          action: "SwitchSide",
          details: { side: "back" },
        });
        this.rotateCard();
      }
    },

    rotateCard() {
      this.isFlipped = !this.isFlipped;
      window.activeCanvas = this.isFlipped
        ? window.backCanvas
        : window.frontCanvas;

      window?.frontCanvas?.discardActiveObject();
      window?.backCanvas?.discardActiveObject();
      window?.helperCanvas?.discardActiveObject();
      window?.frontCanvas?.renderAll();
      window?.backCanvas?.renderAll();
      window?.helperCanvas?.renderAll();
    },

    onRotate(t) {
      var angle = t.target.angle;
      var snapAngle = t.target.snapAngle;
      var snapThreshold = t.target.snapThreshold || snapAngle;
      var rightAngleLocked = Math.ceil(angle / snapAngle) * snapAngle;
      var leftAngleLocked = Math.floor(angle / snapAngle) * snapAngle;

      if (Math.abs(angle - leftAngleLocked) < snapThreshold) {
        t.target.angle = leftAngleLocked;
      } else if (Math.abs(angle - rightAngleLocked) < snapThreshold) {
        t.target.angle = rightAngleLocked;
      }
    },

    handleAppResize() {
      if (document.activeElement.tagName === "TEXTAREA") return;

      if (typeof this.resetCardPan === "function") {
        this.resetCardPan();
      }

      if (
        !window.frontCanvas ||
        !window.frontCanvas.lowerCanvasEl ||
        !window.helperCanvas ||
        !window.helperCanvas.lowerCanvasEl ||
        !window.backCanvas ||
        !window.backCanvas.lowerCanvasEl
      ) {
        return;
      }

      const containerEl = this.$refs.sceneContainer;
      const canvasX = this.getCanvasWidth(containerEl);
      const canvasY = canvasX / canvasAspectRatio;

      if (!canvasX || !canvasY) return;

      window.frontCanvas.setWidth(canvasX);
      window.frontCanvas.setHeight(canvasY);
      window.helperCanvas.setWidth(canvasX + 50);
      window.helperCanvas.setHeight(canvasY + 50);
      window.backCanvas.setWidth(canvasX);
      window.backCanvas.setHeight(canvasY);

      this.canvasWidthPx = canvasX;
      this.canvasHeightPx = canvasY;

      window.frontCanvas.setZoom(canvasX / canvasWidth);
      window.helperCanvas.setZoom(canvasX / canvasWidth);
      window.backCanvas.setZoom(canvasX / canvasWidth);

      // Update base config so scale slider works relative to fitted size
      this.config.frontConfig.canvas.width = canvasX;
      this.config.frontConfig.canvas.height = canvasY;
      this.canvasScale = 1;

      initAligningGuidelines(window.frontCanvas);
      initCenteringGuidelines(window.frontCanvas);
      initAligningGuidelines(window.backCanvas);
      initCenteringGuidelines(window.backCanvas);

      if (typeof this.checkCardOverflow === "function") {
        this.$nextTick(() => this.checkCardOverflow());
      }
    },
  },
};
